import { Button, Link } from '@alfa-retail-react/components';
import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import { useFormContext } from '@alfa-retail-react/components/form-hook';
import { Path } from 'react-hook-form';

import { UnauthorizedException } from 'app/api/exception/UnauthorizedException';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import { ConfirmationPageStore } from 'app/logic/pages/confirmation/ConfirmationPageStore';
import { Section } from 'app/component/Section';
import ListView from 'app/component/list';
import { DocumentReference } from 'app/model/DocumentReference';
import {
    OpenResourceMode,
    DownloadResourceLink,
} from 'app/component/DownloadResourceLink';
import {
    CONDITION_AGREEMENT_FIELD_NAME,
    ConditionAgreementFormItem,
} from 'app/logic/ConditionAgreementFormItem';
import { DownloadDocumentFormItem } from 'app/logic/pages/confirmation/DownloadDocumentFormItem';
import { ConfirmationFormValues } from 'app/logic/pages/confirmation/ConfirmationPage';

export interface DocumentsFormSectionProps {
    pageStore: ConfirmationPageStore;
    unauthorizedHandler: (error: UnauthorizedException) => void;
}

export const DocumentsFormSection = observer(
    ({ pageStore, unauthorizedHandler }: DocumentsFormSectionProps) => {
        const separatedDocumentName =
            pageStore.resources.find((resource) => resource.isSeparated)
                ?.name || '';

        const DOWNLOAD_DOCUMENT_FORM_ITEM_NAME: (
            index: number,
        ) => Path<ConfirmationFormValues> = (index) =>
            `documents.${index}.documentTouched`;

        const { getValues, setValue, trigger } =
            useFormContext<ConfirmationFormValues>();

        const handleDownloadAll = () => {
            return pageStore
                .downloadAll()
                .then((data) => {
                    pageStore.resources.forEach((resource, index) => {
                        const isRead =
                            !resource.isSeparated ||
                            getValues(DOWNLOAD_DOCUMENT_FORM_ITEM_NAME(index));
                        setValue(
                            DOWNLOAD_DOCUMENT_FORM_ITEM_NAME(index),
                            isRead,
                        );
                        trigger(DOWNLOAD_DOCUMENT_FORM_ITEM_NAME(index));
                    });
                    return data;
                })
                .catch(
                    exceptionLogger(
                        'DocumentsFormSection',
                        'pageStore.downloadAll',
                    ),
                )
                .catch((error) => {
                    pageStore.resources.forEach((resource, index) => {
                        if (
                            !getValues(DOWNLOAD_DOCUMENT_FORM_ITEM_NAME(index))
                        ) {
                            setValue(
                                DOWNLOAD_DOCUMENT_FORM_ITEM_NAME(index),
                                false,
                            );
                        }
                    });
                    throw error;
                })
                .catch(unauthorizedHandler);
        };

        return (
            <Section
                className={'documentsFormSection'}
                title={'Документы'}
                content={
                    <>
                        <p className={'mandatoryDocumentsInfo'}>
                            Для оформления заявки обязательно ознакомьтесь с
                            документами ниже
                        </p>
                        <ListView
                            className="resourceList"
                            values={pageStore.resources}
                            children={(
                                value: DocumentReference,
                                index: number,
                            ) => (
                                <Fragment key={value.code}>
                                    <DownloadDocumentFormItem
                                        key={index}
                                        document={value}
                                        index={index}
                                        pageStore={pageStore}
                                        unauthorizedHandler={
                                            unauthorizedHandler
                                        }
                                    />
                                </Fragment>
                            )}
                        />
                        <div className="mandatoryDocumentsTip">
                            <span className="requiredTip">*</span>Обязательно
                            прочтите
                        </div>
                        <DownloadResourceLink
                            className="zipResource"
                            openResourceMode={OpenResourceMode.DOWNLOAD}
                            trigger={
                                <Link underline={false} view="default">
                                    <Button size={'xxs'} className="takeAction">
                                        Скачать документы одним файлом
                                    </Button>
                                </Link>
                            }
                            download={handleDownloadAll}
                        />
                        <div className="downloadAllTip">
                            Все документы, кроме «{separatedDocumentName}»
                        </div>
                        <ConditionAgreementFormItem<
                            ConfirmationFormValues,
                            unknown
                        >
                            name={CONDITION_AGREEMENT_FIELD_NAME}
                            checked={pageStore.conditionAgreement}
                            defaultValue={false}
                            label={
                                'Я ознакомлен(-а) и согласен(-на) с перечисленными документами'
                            }
                            onChange={(checked) =>
                                (pageStore.conditionAgreement = checked)
                            }
                        />
                    </>
                }
            />
        );
    },
);
